// @ts-nocheck
/**
 * API Builder — key/value or raw editor for request URL, headers, body.
 * Accepts an `initialRawBody` and an `onClear` callback (differs from
 * sms-webhook ApiBuilder). Body tab renders for POST/PUT/PATCH/DELETE.
 * Headers use an edit-in-place table (FiEdit/FiTrash2). `isValidCurl` also
 * matches -H/--header/-X/--request prefixes. `sendRequest` calls onResponse
 * with (dataStr, headers, { status, statusText }). Parent owns response UI.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { PROXY_URL } from '@/utilities/constant';
import { parseCurl, parseNestedStrings } from './apiBuilderUtils';
import { toast } from 'sonner';
import './ApiBuilder.css';

const Notify = {
  notify: (msg: any) => toast.warning(msg),
};

const ApiBuilder = ({
  initialUrl = '',
  initialMethod = 'GET',
  initialHeaders = [{ key: '', value: '' }],
  initialBody = [{ key: '', value: '' }],
  initialRawBody = '',
  onUseUrl,
  onMethod,
  onResponse,
  onStateChange,
  onClear,
}: any) => {
  const [url, setUrl] = useState(initialUrl);
  const [headers, setHeaders] = useState<any[]>(initialHeaders);
  const [body, setBody] = useState<any[]>(initialBody);
  const [rawHeaders, setRawHeaders] = useState('');
  const [rawBody, setRawBody] = useState(initialRawBody);
  const [headerMode, setHeaderMode] = useState('key-value');
  const [bodyMode, setBodyMode] = useState(initialRawBody ? 'raw' : 'key-value');
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const bodyModeInitialized = useRef(false);
  const showBodyTab = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(initialMethod);
  const [urlError, setUrlError] = useState('');
  const [headerErrors, setHeaderErrors] = useState<any>({});
  const [bodyErrors, setBodyErrors] = useState<any>({});
  const [editingHeaderIndex, setEditingHeaderIndex] = useState<any>(null);
  const [editingHeaderKey, setEditingHeaderKey] = useState('');
  const [editingHeaderValue, setEditingHeaderValue] = useState('');

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        url,
        method: initialMethod,
        headers,
        body,
        rawBody,
        bodyMode,
        headerMode,
        rawHeaders,
      });
    }
  }, [url, initialMethod, headers, body, rawBody, bodyMode, headerMode, rawHeaders, onStateChange]);

  useEffect(() => {
    if (initialRawBody) {
      setRawBody(initialRawBody);
      if (!bodyModeInitialized.current) {
        setBodyMode('raw');
        bodyModeInitialized.current = true;
      }
    }
  }, [initialRawBody]);

  const handleAddHeader = () => {
    const last = headers[headers.length - 1];
    const key = last.key?.trim();
    const value = last.value?.trim();
    if (!key || !value) return;
    if (!/^[A-Za-z0-9_-]+$/.test(key)) return;
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const handleHeaderChange = (i: number, field: string, value: string) => {
    const updated = [...headers];
    updated[i][field] = value;
    setHeaders(updated);

    const errors: any = { ...headerErrors };
    const key = updated[i].key?.trim();
    const val = updated[i].value?.trim();

    if (!key && !val) {
      delete errors[i];
    } else if (!key) {
      errors[i] = 'Key cannot be empty';
    } else if (!/^[A-Za-z0-9_-]+$/.test(key)) {
      errors[i] = 'Key can only contain letters, numbers, _ and -';
    } else if (!val) {
      errors[i] = 'Value cannot be empty';
    } else {
      delete errors[i];
    }

    setHeaderErrors(errors);
  };

  const removeHeader = (index: number) => {
    const updated = headers.filter((_, i) => i !== index);
    setHeaders(updated.length ? updated : [{ key: '', value: '' }]);
  };

  const handleHeaderModeChange = (mode: string) => {
    if (mode === 'raw') {
      const obj = headers.reduce((acc: any, { key, value }: any) => {
        if (key) acc[key] = value;
        return acc;
      }, {});
      setRawHeaders(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : '');
      setHeaderMode('raw');
    } else {
      try {
        const parsed = JSON.parse(rawHeaders || '{}');
        const arr = Object.entries(parsed).map(([key, val]) => ({
          key,
          value: typeof val === 'string' ? val : JSON.stringify(val),
        }));
        setHeaders(arr.length ? arr : [{ key: '', value: '' }]);
        setHeaderMode('key-value');
      } catch {
        Notify.notify('Invalid JSON in raw headers');
      }
    }
  };

  const handleAddBody = () => {
    const last = body[body.length - 1];
    const key = last.key?.trim();
    const value = last.value?.trim();

    if (!key && !value) return;

    if (!key) {
      setBodyErrors((prev: any) => ({ ...prev, [body.length - 1]: 'Key cannot be empty' }));
      return;
    }

    if (!/^[A-Za-z0-9_-]+$/.test(key)) {
      setBodyErrors((prev: any) => ({
        ...prev,
        [body.length - 1]: 'Key can only contain letters, numbers, _ and -',
      }));
      return;
    }

    if (!value) {
      setBodyErrors((prev: any) => ({ ...prev, [body.length - 1]: 'Value cannot be empty' }));
      return;
    }

    setBodyErrors((prev: any) => {
      const updated = { ...prev };
      delete updated[body.length - 1];
      return updated;
    });

    setBody([...body, { key: '', value: '' }]);
  };

  const handleBodyChange = (i: number, field: string, value: string) => {
    const updated = [...body];
    updated[i][field] = value;
    setBody(updated);

    const errors: any = { ...bodyErrors };
    const { key, value: val } = updated[i];

    if (field === 'key') {
      if (isFalsyValue(value)) {
        errors[i] = 'Key cannot be empty';
      } else if (!isValidKey(value)) {
        errors[i] = 'Key cannot contain spaces or special characters';
      } else if (!isFalsyValue(val)) {
        delete errors[i];
      }
    }

    if (field === 'value') {
      if (isFalsyValue(value)) {
        errors[i] = 'Value cannot be empty';
      } else if (isValidKey(key)) {
        delete errors[i];
      }
    }

    setBodyErrors(errors);
  };

  const handleUrlChange = (e: any) => {
    const value = e.target.value;
    setUrl(value);

    const cleaned = value
      .trim()
      .replace(/^\$+\s*/, '')
      .replace(/^[Cc][Uu][Rr][Ll]\b/, 'curl');

    const looksLikeCurl =
      cleaned.toLowerCase().startsWith('curl') ||
      /(?:-H|--header|-X|--request)\s+/.test(cleaned);

    if (looksLikeCurl) {
      handleClear();
      const parsed = parseCurl(cleaned);
      setUrl(parsed.url ?? '');
      if (parsed.method && onMethod) {
        onMethod(parsed.method);
      }
      setHeaderMode('key-value');
      setBodyMode('key-value');
      setRawHeaders('');
      setRawBody('');

      const headerArray = Object.entries(parsed.headers || {}).map(([key, value]) => ({
        key,
        value,
      }));
      setHeaders(headerArray.length ? headerArray : [{ key: '', value: '' }]);

      if (parsed.body) {
        try {
          const bodyObj = JSON.parse(parsed.body);
          const bodyArray = Object.entries(bodyObj).map(([key, value]: any) => ({
            key,
            value:
              value && typeof value === 'object' && !Array.isArray(value)
                ? JSON.stringify(value)
                : value,
          }));
          setBody(bodyArray.length ? bodyArray : [{ key: '', value: '' }]);
          setBodyMode('key-value');
          if (String(parsed.method || '').toUpperCase() === 'POST' && onResponse) {
            onResponse(JSON.stringify(bodyObj, null, 2));
          }
        } catch {
          const params = new URLSearchParams(parsed.body);
          const bodyArray: any[] = [];
          const bodyObj: any = {};
          params.forEach((value, key) => {
            bodyArray.push({ key, value });
            bodyObj[key] = value;
          });
          setBody(bodyArray.length ? bodyArray : [{ key: '', value: '' }]);
          setBodyMode('key-value');
          if (String(parsed.method || '').toUpperCase() === 'POST' && onResponse) {
            onResponse(JSON.stringify(bodyObj, null, 2));
          }
        }
      } else {
        setBody([{ key: '', value: '' }]);
      }
    }

    if (!value.trim()) {
      setUrlError('Request URL is required');
    } else if (!isValidUrl(value) && !isValidCurl(value)) {
      setUrlError('Enter a valid URL or valid cURL');
    } else {
      setUrlError('');
    }
  };

  const sendRequest = async () => {
    if (!url) {
      Notify.notify('Please enter a URL');
      return null;
    }
    setLoading(true);
    const abortController = new AbortController();
    controllerRef.current = abortController;

    let headerObj: any = {};

    if (headerMode === 'raw') {
      try {
        headerObj = JSON.parse(rawHeaders || '{}');
      } catch {
        Notify.notify('Raw headers must be valid JSON');
        setLoading(false);
        return;
      }
    } else {
      headerObj = headers.reduce((acc: any, { key, value }: any) => {
        if (key) acc[key] = value;
        return acc;
      }, {});
    }

    let requestBody: any = null;
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(initialMethod)) {
      const contentTypeKey = Object.keys(headerObj).find(
        (k) => k.toLowerCase() === 'content-type',
      );
      const isJson = contentTypeKey
        ? headerObj[contentTypeKey].includes('application/json')
        : false;

      if (bodyMode === 'raw') {
        requestBody = rawBody;
      } else if (isJson) {
        const bodyObj = body.reduce((acc: any, { key, value }: any) => {
          if (!key) return acc;
          acc[key] = parseNestedStrings(value);
          return acc;
        }, {});
        requestBody = JSON.stringify(bodyObj);
      } else {
        const formData = new URLSearchParams();
        body.forEach(({ key, value }: any) => {
          if (key) formData.append(key, value);
        });
        requestBody = formData.toString();

        if (!contentTypeKey) {
          headerObj['Content-Type'] = 'application/x-www-form-urlencoded';
        }
      }
    }

    try {
      const res = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          method: initialMethod,
          headers: JSON.stringify(headerObj),
          body: requestBody,
        }),
        signal: abortController.signal,
      });

      const contentType = res.headers.get('content-type');
      const data: any =
        contentType && contentType.includes('application/json')
          ? await res.json()
          : await res.text();

      const responseObj = {
        status: data.status,
        statusText: data.statusText,
        headers: data.headers,
        data: data.data,
      };

      setResponse(responseObj);
      if (onResponse) {
        const dataStr =
          typeof responseObj.data === 'object'
            ? JSON.stringify(responseObj.data, null, 2)
            : String(responseObj.data || '');
        onResponse(dataStr, responseObj.headers || {}, {
          status: responseObj.status,
          statusText: responseObj.statusText,
        });
      }
      return responseObj;
    } catch (err: any) {
      if (err.name === 'AbortError') Notify.notify('Request aborted!');
      else Notify.notify(err.message);
      return null;
    } finally {
      setLoading(false);
      controllerRef.current = null;
    }
  };

  const stopRequest = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  };

  const removeBody = (index: number) => {
    const updated = body.filter((_, i) => i !== index);
    setBody(updated.length ? updated : [{ key: '', value: '' }]);
  };

  const handleClear = () => {
    setUrl('');
    setHeaders([{ key: '', value: '' }]);
    setBody([{ key: '', value: '' }]);
    setRawHeaders('');
    setRawBody('');
    setHeaderMode('key-value');
    setBodyMode('key-value');
    setResponse(null);
    if (onClear) onClear();
  };

  const handleSubmit = async () => {
    if (!url) return;
    if (onUseUrl) onUseUrl(url);
    if (onResponse && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(initialMethod)) {
      const bodyString =
        bodyMode === 'raw'
          ? rawBody
          : JSON.stringify(
              body.reduce((acc: any, { key, value }: any) => {
                if (!key) return acc;
                acc[key] = parseNestedStrings(value);
                return acc;
              }, {}),
              null,
              2,
            );
      onResponse(bodyString);
    }
  };

  const handleBodyModeChange = (mode: string) => {
    if (mode === 'raw') {
      const obj = body.reduce((acc: any, { key, value }: any) => {
        if (!key) return acc;
        acc[key] = parseNestedStrings(value);
        return acc;
      }, {});
      setRawBody(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : '');
      setBodyMode('raw');
    } else if (mode === 'key-value') {
      try {
        const parsed = JSON.parse(rawBody || '{}');
        const arr = Object.entries(parsed).map(([key, val]) => ({
          key,
          value: typeof val === 'string' ? val : JSON.stringify(val),
        }));
        setBody(arr.length ? arr : [{ key: '', value: '' }]);
        setBodyMode('key-value');
      } catch (err) {
        Notify.notify('Invalid JSON in raw body');
      }
    }
  };

  const isValidUrl = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const isValidCurl = (value: string) => {
    const v = value.trim().replace(/^\$+\s*/, '');
    return (
      v.toLowerCase().startsWith('curl') ||
      /(?:-H|--header|-X|--request)\s+/.test(v)
    );
  };

  const isValidKey = (key: string) => {
    const trimmed = key?.trim();
    const keyRegex = /^[A-Za-z0-9_-]+$/;
    return keyRegex.test(trimmed);
  };

  const isFalsyValue = (val: string) => {
    return !val || val.trim() === '';
  };

  const startEditHeader = (idx: number, h: any) => {
    setEditingHeaderIndex(idx);
    setEditingHeaderKey(h.key);
    setEditingHeaderValue(h.value);
  };

  const saveHeaderEdit = (idx: number) => {
    const key = editingHeaderKey.trim();
    if (!key) return;
    const updated = [...headers];
    updated[idx] = { key, value: editingHeaderValue };
    setHeaders(updated);
    setEditingHeaderIndex(null);
    setEditingHeaderKey('');
    setEditingHeaderValue('');
  };

  const cancelHeaderEdit = (idx: number, h: any) => {
    if (!h.key?.trim()) {
      const updated = headers.filter((_, i) => i !== idx);
      setHeaders(updated.length ? updated : [{ key: '', value: '' }]);
    }
    setEditingHeaderIndex(null);
    setEditingHeaderKey('');
    setEditingHeaderValue('');
  };

  const handleAddHeaderFromTable = () => {
    const newIdx = headers.length;
    setHeaders([...headers, { key: '', value: '' }]);
    setEditingHeaderIndex(newIdx);
    setEditingHeaderKey('');
    setEditingHeaderValue('');
  };

  const removeHeaderFromTable = (idx: number) => {
    const updated = headers.filter((_, i) => i !== idx);
    setHeaders(updated.length ? updated : [{ key: '', value: '' }]);
    if (editingHeaderIndex === idx) {
      setEditingHeaderIndex(null);
      setEditingHeaderKey('');
      setEditingHeaderValue('');
    }
  };

  return (
    <div className="mt-4 api-tester">
      <h4 className="mb-4 text-lg font-bold border-b border-border pb-2">API Builder</h4>

      <div className="mb-3 space-y-2">
        <label className="text-sm font-semibold leading-none">
          Request URL <span className="text-destructive mx-1">*</span>
        </label>
        <textarea
          className="w-full rounded-md border border-border/60 bg-[hsl(210_40%_98%)] px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring transition-colors resize-y dark:bg-[hsl(222_47%_10%)]"
          rows={3}
          placeholder="Enter URL or paste full cURL here"
          value={url}
          onChange={handleUrlChange}
          style={{ fontSize: '1rem', width: '100%' }}
        />
        {urlError && (
          <div className="text-xs text-destructive mt-1">{urlError}</div>
        )}
      </div>

      {/* Header quick-edit table */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-semibold leading-none">Request Headers</label>
          <Button type="button" variant="outline" size="sm" onClick={handleAddHeaderFromTable}>
            + Add Header
          </Button>
        </div>
        {(headers.some((h: any) => h.key?.trim()) || editingHeaderIndex !== null) && (
          <div className="rounded-md border border-border overflow-hidden">
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              <table
                className="w-full text-sm"
                style={{ tableLayout: 'fixed' }}
              >
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium" style={{ width: '25%' }}>Key</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ width: '55%' }}>Value</th>
                    <th className="text-center px-3 py-2 font-medium" style={{ width: '20%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((h: any, idx: number) => {
                    const isEditing = editingHeaderIndex === idx;
                    if (!h.key?.trim() && !isEditing) return null;
                    return isEditing ? (
                      <tr key={idx} className="border-t border-border">
                        <td className="px-2 py-1">
                          <Input
                            className="h-8"
                            placeholder="Key"
                            value={editingHeaderKey}
                            autoFocus
                            onChange={(e) => setEditingHeaderKey(e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            className="h-8"
                            placeholder="Value"
                            value={editingHeaderValue}
                            onChange={(e) => setEditingHeaderValue(e.target.value)}
                          />
                        </td>
                        <td className="text-center px-2 py-1">
                          <Button
                            type="button"
                            size="sm"
                            className="mr-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => saveHeaderEdit(idx)}
                          >
                            ✓
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => cancelHeaderEdit(idx, h)}
                          >
                            ✗
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={idx} className="border-t border-border">
                        <td className="px-3 py-2">{h.key}</td>
                        <td
                          className="px-3 py-2"
                          style={{
                            wordBreak: 'break-all',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {h.value}
                        </td>
                        <td className="text-center px-3 py-2">
                          <FiEdit
                            size={17}
                            className="text-blue-600 cursor-pointer mr-2 inline"
                            title="Edit"
                            onClick={() => startEditHeader(idx, h)}
                          />
                          <FiTrash2
                            size={17}
                            className="text-destructive cursor-pointer inline"
                            title="Remove"
                            onClick={() => removeHeaderFromTable(idx)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showBodyTab && (
        <div className="mb-3">
          <label className="text-sm font-semibold leading-none mb-2 block">Request Body</label>
          <div className="tab-section">
            <div className="mb-2 flex gap-2">
              <Button
                type="button"
                variant={bodyMode === 'key-value' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBodyModeChange('key-value')}
              >
                Key-Value
              </Button>
              <Button
                type="button"
                variant={bodyMode === 'raw' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleBodyModeChange('raw')}
              >
                Raw
              </Button>
            </div>

            {bodyMode === 'key-value' ? (
              <div>
                {body.map((b: any, i: number) => (
                  <div key={i}>
                    <div className="row mb-2">
                      <div className="col-md-5">
                        <Input
                          placeholder="Key"
                          value={b.key}
                          onChange={(e) => handleBodyChange(i, 'key', e.target.value)}
                        />
                      </div>
                      <div className="col-md-5">
                        <Input
                          placeholder="Value"
                          value={b.value}
                          onChange={(e) => handleBodyChange(i, 'value', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2 text-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removeBody(i)}
                        >
                          −
                        </Button>
                      </div>
                    </div>
                    {bodyErrors[i] && (
                      <div className="text-xs text-destructive">{bodyErrors[i]}</div>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={handleAddBody}>
                  + Add Body Parameter
                </Button>
              </div>
            ) : (
              <div>
                <textarea
                  className="w-full rounded-md border border-border/60 bg-[hsl(210_40%_98%)] px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring transition-colors resize-y dark:bg-[hsl(222_47%_10%)]"
                  rows={8}
                  placeholder="Enter raw JSON body here"
                  value={rawBody}
                  onChange={(e) => setRawBody(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {loading ? (
          <Button type="button" variant="destructive" onClick={stopRequest}>
            Stop
          </Button>
        ) : (
          <>
            <Button type="button" onClick={sendRequest} disabled={loading}>
              Test
            </Button>
            {url && (
              <Button
                type="button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleSubmit}
                disabled={loading}
              >
                Submit
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleClear}>
              Clear
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ApiBuilder;
